import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../common/Button';
import FullBlockingModal from '../../common/FullBlockingModal';
import { useAdminStore } from '../../../stores/adminStore';

interface GenerateAuthLinkModalProfile {
  id: string;
  first_name?: string;
  last_name?: string;
}

interface GenerateAuthLinkModalProps {
  profile: GenerateAuthLinkModalProfile;
  onClose: () => void;
}

const GenerateAuthLinkModal: React.FC<GenerateAuthLinkModalProps> = ({ profile, onClose }) => {
  const { t } = useTranslation(['admin', 'common']);
  const { generateAuthLink } = useAdminStore();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<{
    actionLink: string;
    linkType: 'magiclink' | 'recovery';
  } | null>(null);

  const username = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await generateAuthLink(profile.id);
        if (!cancelled) {
          setResult(data);
        }
      } catch (err) {
        toast.error(
          (err as Error).message ||
            t('generateAuthLink.error', {
              ns: 'admin',
              defaultValue: 'Could not generate a link for this user.',
            })
        );
        onClose();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  const handleCopy = async () => {
    if (!result) return;

    try {
      await navigator.clipboard.writeText(result.actionLink);
      toast.success(t('generateAuthLink.copied', { ns: 'admin', defaultValue: 'Link copied' }));
    } catch (err) {
      console.error('Failed to copy auth link', err);
      toast.error(
        t('generateAuthLink.copyError', {
          ns: 'admin',
          defaultValue: 'Could not copy this link.',
        })
      );
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[32rem]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-secondary">
          {t('generateAuthLink.title', { ns: 'admin', defaultValue: 'Generate login link' })}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          type="button"
        >
          <X size={22} />
        </button>
      </div>

      {username && <p className="text-sm text-gray-700 mb-2">{username}</p>}

      {loading && (
        <p className="text-sm text-gray-600">
          {t('generateAuthLink.loading', {
            ns: 'admin',
            defaultValue: 'Generating link...',
          })}
        </p>
      )}

      {!loading && result && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-700">
            {result.linkType === 'magiclink'
              ? t('generateAuthLink.magiclinkHelp', {
                  ns: 'admin',
                  defaultValue: 'This link lets the user confirm their account and sign in.',
                })
              : t('generateAuthLink.recoveryHelp', {
                  ns: 'admin',
                  defaultValue: 'This link lets the user set a new password.',
                })}
          </p>

          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <input
              type="text"
              readOnly
              value={result.actionLink}
              onFocus={e => e.target.select()}
              className="w-full min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            />
            <Button
              variant="primary"
              type="button"
              onClick={handleCopy}
              className="!w-full md:!w-auto flex items-center justify-center gap-2 rounded-full px-6 py-2"
            >
              <Copy size={16} />
              {t('generateAuthLink.copyButton', { ns: 'admin', defaultValue: 'Copy link' })}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-row justify-end mt-8">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default GenerateAuthLinkModal;
